import { useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  useCreatePreboardingField,
  useDeletePreboardingField,
  useDeletePreboardingSection,
  useReorderPreboardingFields,
  useUpdatePreboardingField,
  useUpdatePreboardingSection,
} from '@/hooks/use-preboarding-form-builder';
import type {
  CreatePreboardingFormFieldInput,
  PreboardingFormField,
  PreboardingFormSection,
} from '@hygge/shared';
import { extractApiError } from './builder-utils';
import { FieldEditorDialog } from './field-editor-dialog';
import { FieldRow } from './field-row';
import { SectionEditorDialog, type SectionDraft } from './section-editor-dialog';

interface SectionCardProps {
  formId: string;
  section: PreboardingFormSection;
}

export function SectionCard({ formId, section }: SectionCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  const updateSection = useUpdatePreboardingSection(formId);
  const deleteSection = useDeletePreboardingSection(formId);
  const createField = useCreatePreboardingField(formId);
  const updateField = useUpdatePreboardingField(formId);
  const deleteField = useDeletePreboardingField(formId);
  const reorderFields = useReorderPreboardingFields(formId);

  const [editSectionOpen, setEditSectionOpen] = useState(false);
  const [deleteSectionOpen, setDeleteSectionOpen] = useState(false);
  const [createFieldOpen, setCreateFieldOpen] = useState(false);
  const [editFieldTarget, setEditFieldTarget] = useState<PreboardingFormField | null>(null);
  const [deleteFieldTarget, setDeleteFieldTarget] = useState<PreboardingFormField | null>(null);

  const sortedFields = useMemo(
    () =>
      [...section.fields].sort(
        (a, b) =>
          a.position - b.position ||
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [section.fields],
  );

  const fieldSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleFieldDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = sortedFields.findIndex((f) => f.id === active.id);
      const newIndex = sortedFields.findIndex((f) => f.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(sortedFields, oldIndex, newIndex);
      reorderFields.mutate(
        { sectionId: section.id, fieldIds: reordered.map((f) => f.id) },
        { onError: (err) => toast.error(extractApiError(err, 'Failed to reorder fields')) },
      );
    },
    [sortedFields, reorderFields, section.id],
  );

  const handleEditSection = (draft: SectionDraft) => {
    updateSection.mutate(
      { sectionId: section.id, ...draft },
      {
        onSuccess: () => setEditSectionOpen(false),
        onError: (err) => toast.error(extractApiError(err, 'Failed to update section')),
      },
    );
  };

  const handleDeleteSection = () => {
    deleteSection.mutate(section.id, {
      onSuccess: () => setDeleteSectionOpen(false),
      onError: (err) => toast.error(extractApiError(err, 'Failed to delete section')),
    });
  };

  const handleCreateField = (input: CreatePreboardingFormFieldInput) => {
    createField.mutate(
      { sectionId: section.id, ...input },
      {
        onSuccess: () => setCreateFieldOpen(false),
        onError: (err) => toast.error(extractApiError(err, 'Failed to add field')),
      },
    );
  };

  const handleUpdateField = (input: CreatePreboardingFormFieldInput) => {
    if (!editFieldTarget) return;
    // kind is immutable — send only the editable presentation + addressing.
    updateField.mutate(
      {
        sectionId: section.id,
        fieldId: editFieldTarget.id,
        label: input.label,
        helperText: input.helperText,
        required: input.required,
        mappingKey: input.mappingKey,
        targetFolder: input.targetFolder,
      },
      {
        onSuccess: () => setEditFieldTarget(null),
        onError: (err) => toast.error(extractApiError(err, 'Failed to update field')),
      },
    );
  };

  const handleDeleteField = () => {
    if (!deleteFieldTarget) return;
    deleteField.mutate(
      { sectionId: section.id, fieldId: deleteFieldTarget.id },
      {
        onSuccess: () => setDeleteFieldTarget(null),
        onError: (err) => toast.error(extractApiError(err, 'Failed to delete field')),
      },
    );
  };

  const hasFields = sortedFields.length > 0;

  return (
    <Card ref={setNodeRef} style={style} className="overflow-hidden">
      <CardHeader className="group flex flex-row items-start justify-between space-y-0 border-b border-border/50 bg-muted/30 px-5 py-3">
        <div className="flex items-start gap-2 min-w-0">
          <button
            type="button"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
            className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground opacity-40 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing touch-none"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-foreground">{section.title}</h3>
            {section.description && (
              <p className="mt-0.5 text-xs text-muted-foreground">{section.description}</p>
            )}
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Section actions"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onSelect={() => setEditSectionOpen(true)}>
              <Pencil className="mr-2 h-3.5 w-3.5" />
              Edit section
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => setDeleteSectionOpen(true)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete section
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>

      <CardContent className="space-y-2 p-4">
        {hasFields ? (
          <DndContext
            sensors={fieldSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleFieldDragEnd}
          >
            <SortableContext items={sortedFields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {sortedFields.map((field) => (
                  <FieldRow
                    key={field.id}
                    field={field}
                    onEdit={() => setEditFieldTarget(field)}
                    onDelete={() => setDeleteFieldTarget(field)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <p className="text-xs italic text-muted-foreground">
            No fields yet. Add an employee field or a document upload.
          </p>
        )}

        <button
          type="button"
          onClick={() => setCreateFieldOpen(true)}
          className={cn(
            'flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border/70',
            'px-3 py-2 text-[11px] font-medium text-muted-foreground',
            'hover:border-foreground/40 hover:text-foreground hover:bg-muted/40 transition-colors',
          )}
        >
          <Plus className="h-3 w-3" />
          Add field
        </button>
      </CardContent>

      <SectionEditorDialog
        open={editSectionOpen}
        mode="edit"
        section={section}
        isSubmitting={updateSection.isPending}
        onOpenChange={setEditSectionOpen}
        onSubmit={handleEditSection}
      />

      <FieldEditorDialog
        open={createFieldOpen}
        mode="create"
        isSubmitting={createField.isPending}
        onOpenChange={setCreateFieldOpen}
        onSubmit={handleCreateField}
      />

      <FieldEditorDialog
        open={editFieldTarget !== null}
        mode="edit"
        field={editFieldTarget}
        isSubmitting={updateField.isPending}
        onOpenChange={(open) => {
          if (!open) setEditFieldTarget(null);
        }}
        onSubmit={handleUpdateField}
      />

      <AlertDialog open={deleteSectionOpen} onOpenChange={setDeleteSectionOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{section.title}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the section and all {sortedFields.length} of its fields. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteSection();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteFieldTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteFieldTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteFieldTarget?.label}”?</AlertDialogTitle>
            <AlertDialogDescription>
              The field is removed from this section. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteField();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
